import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Switch,
  Textarea,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCreateJournalEntry, useUpdateJournalEntry } from '../../../api/created/journal.ts';
import { getGetPendingTasksQueryKey } from '../../../api/created/tasks.ts';
import { getCurrentUser, useCurrentUser } from '../../../api/created/users.ts';
import { getGetJournalByPatientQueryKey } from '../../../api/generated/journal-controller/journal-controller.ts';
import type { FileDetailsDto, UserDto } from '../../../api/generated/model';
import { JournalDtoTopic } from '../../../api/generated/model';
import { useGetPatientById } from '../../../api/generated/patient-controller/patient-controller.ts';
import { FileInput } from '../../../components/form/FileInput.tsx';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../components/form/GermanDateInput.tsx';
import { getCurrentDate } from '../../../utils/date.ts';
import { blobToBase64 } from '../../../utils/file.ts';
import { getJournalTopicName } from '../../../utils/journal.ts';
import { FileLink } from '../FileLink.tsx';
import { SelectUser } from '../SelectUser.tsx';

export interface EditableJournalEntryData {
  id: string;
  topic: JournalDtoTopic;
  notes: string;
  isTodo: boolean;
  due?: string | null;
  assignee?: UserDto | null;
  file?: FileDetailsDto;
}

interface FormValues {
  note: string;
  topic: JournalDtoTopic;
  due: string;
  isTodo: boolean;
  assignee: UserDto | null;
  file: File | null;
}

export function CreateJournalEntryDialog({
  patientId,
  preselectedTopic,
  open,
  onClose,
  disabledTopics,
  editableJournalEntry,
}: {
  patientId: string;
  preselectedTopic: JournalDtoTopic;
  open: boolean;
  onClose: () => void;
  disabledTopics?: JournalDtoTopic[];
  editableJournalEntry?: EditableJournalEntryData;
}) {
  const queryClient = useQueryClient();
  const { data: patient } = useGetPatientById(patientId, { query: { enabled: open } });
  const { data: currentUser } = useCurrentUser({ enabled: open });
  const isEditMode = !!editableJournalEntry;

  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(preselectedTopic, editableJournalEntry),
  });

  const createJournalEntryMutation = useCreateJournalEntry({
    mutation: {
      onSuccess: () => {
        toast.success('Notiz wurde erfolgreich gespeichert');
        void queryClient.invalidateQueries({
          queryKey: getGetJournalByPatientQueryKey({ patientId }),
        });
        void queryClient.invalidateQueries({ queryKey: getGetPendingTasksQueryKey() });
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim Speichern der Notiz ist ein Fehler aufgetreten');
      },
    },
  });

  const updateJournalEntryMutation = useUpdateJournalEntry({
    mutation: {
      onSuccess: () => {
        toast.success('Notiz wurde erfolgreich gespeichert');
        void queryClient.invalidateQueries({
          queryKey: getGetJournalByPatientQueryKey({ patientId }),
        });
        void queryClient.invalidateQueries({ queryKey: getGetPendingTasksQueryKey() });
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim Speichern der Notiz ist ein Fehler aufgetreten');
      },
    },
  });

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(preselectedTopic, editableJournalEntry));
    }
  }, [editableJournalEntry, open, preselectedTopic, reset]);

  const handleOnClose = () => {
    reset(getDefaultValues(preselectedTopic, editableJournalEntry));
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 700 }}>
        <ModalClose />
        <Typography level={'h4'}>
          {isEditMode ? 'Notiz bearbeiten' : 'Notiz erfassen'} ({patient?.lastName} {patient?.firstName})
        </Typography>
        <Divider />
        <form
          onSubmit={handleSubmit(async (data) => {
            const fallbackUser = data.assignee ? undefined : (currentUser ?? (await getCurrentUser()));
            const payload = {
              patientId: patientId,
              topic: data.topic,
              notes: data.note,
              isTodo: data.isTodo,
              due: data.due || getCurrentDate(),
              assignedEmployeeId: data.assignee?.id ?? fallbackUser?.id ?? null,
              file: data.file
                ? {
                    fileName: data.file.name,
                    file: await blobToBase64(data.file),
                  }
                : undefined,
            };

            if (editableJournalEntry) {
              updateJournalEntryMutation.mutate({ id: editableJournalEntry.id, data: payload });
              return;
            }

            createJournalEntryMutation.mutate({ data: payload });
          })}>
          <Stack gap={2}>
            <FormControl error={!!errors.topic}>
              <FormLabel>
                <Typography>Thema *</Typography>
              </FormLabel>
              <Controller
                name={'topic'}
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Select value={value} disabled={isEditMode} onChange={(_, value) => onChange(value)}>
                    {Object.values(JournalDtoTopic)
                      .filter((topic) => isEditMode || !disabledTopics?.includes(topic))
                      .map((topic) => (
                        <Option value={topic} key={topic}>
                          {getJournalTopicName(topic)}
                        </Option>
                      ))}
                  </Select>
                )}
              />
              {errors.topic && <FormHelperText>Thema muss ausgefüllt werden</FormHelperText>}
            </FormControl>
            <FormControl error={!!errors.due}>
              <FormLabel>
                <Typography>Fälligkeitsdatum</Typography>
              </FormLabel>
              <GermanDateInput control={control} name="due" />
            </FormControl>
            <FormControl error={!!errors.note}>
              <FormLabel>
                <Typography>Notiz *</Typography>
              </FormLabel>
              <Textarea {...register('note', { required: true })} disabled={isEditMode} minRows={2} maxRows={4} />
              {errors.note && <FormHelperText>Notiz muss ausgefüllt werden</FormHelperText>}
            </FormControl>
            <FormControl>
              <FormLabel>
                <Typography>Datei</Typography>
              </FormLabel>
              <Controller
                name={'file'}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FileInput
                    value={value}
                    onChange={onChange}
                    accept={'application/pdf'}
                    placeholder={'Datei hochladen'}
                    disabled={isEditMode}
                  />
                )}
              />
              {editableJournalEntry?.file && <FileLink fileDetails={editableJournalEntry.file} />}
            </FormControl>

            <div>
              <Controller
                name="isTodo"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Switch
                    checked={value}
                    onChange={onChange}
                    disabled={isEditMode}
                    endDecorator={'Handlung erforderlich (ist ToDo)'}
                    sx={{ paddingTop: '1rem' }}
                  />
                )}
              />
            </div>
            <FormControl error={!!errors.assignee}>
              <FormLabel>
                <Typography>Zuständigkeit</Typography>
              </FormLabel>
              <Controller
                name="assignee"
                control={control}
                render={({ field: { onChange, value } }) => <SelectUser onChange={onChange} value={value} />}
              />
            </FormControl>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={createJournalEntryMutation.isPending || updateJournalEntryMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

function getDefaultValues(
  preselectedTopic: JournalDtoTopic,
  editableJournalEntry?: EditableJournalEntryData,
): FormValues {
  return {
    note: editableJournalEntry?.notes ?? '',
    topic: editableJournalEntry?.topic ?? preselectedTopic,
    due: getDateOnlyValue(editableJournalEntry?.due),
    isTodo: editableJournalEntry?.isTodo ?? false,
    assignee: editableJournalEntry?.assignee ?? null,
    file: null,
  };
}

function getDateOnlyValue(value?: string | null) {
  const match = value?.trim().match(/^(\d{4}-\d{2}-\d{2})(?:$|T)/);

  return match?.[1] ?? '';
}
